/**
 * Auth API
 * Аналог lib/features/auth/data/datasources/auth_remote_data_source.dart
 */
import { apiClient } from '@/api'
import type {
  ImpersonationTokenResponse,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  UpdateUserSettingsParams,
  User,
  UserSettings,
} from '../types/auth.types'

export const authApi = {
  /**
   * Login - аналог AuthRemoteDataSource.login()
   * POST /api/v1/auth/login
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/v1/auth/login', {
      username: credentials.email,
      password: credentials.password,
    })
    return response.data
  },

  /**
   * Logout
   * POST /api/v1/auth/logout
   */
  async logout(): Promise<void> {
    await apiClient.post('/api/v1/auth/logout')
  },

  /**
   * Обмен одноразового кода из ссылки «Войти как» на токен вкладки просмотра
   * (DEV-2400). POST /api/v1/auth/impersonation/exchange — публичный.
   */
  async exchangeImpersonationCode(code: string): Promise<ImpersonationTokenResponse> {
    const response = await apiClient.post<ImpersonationTokenResponse>(
      '/api/v1/auth/impersonation/exchange',
      { code },
    )
    return response.data
  },

  /** Завершить сеанс просмотра. POST /api/v1/auth/impersonation/end */
  async endImpersonation(): Promise<void> {
    await apiClient.post('/api/v1/auth/impersonation/end')
  },

  /**
   * Refresh Token - аналог AuthRemoteDataSource.refreshToken()
   * POST /api/v1/auth/refresh
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>('/api/v1/auth/refresh', {
      refreshToken,
    })
    return response.data
  },

  /**
   * Change Password — смена пароля текущим пользователем (DEV-705)
   * POST /api/v1/auth/password/change
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/api/v1/auth/password/change', {
      oldPassword,
      newPassword,
    })
  },

  /**
   * Get Current User - аналог GetCurrentUser usecase
   * GET /api/v1/auth/users/me
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/api/v1/auth/me')
    return response.data
  },

  /**
   * Get User Settings
   * GET /api/v1/auth/settings
   */
  async getUserSettings(): Promise<UserSettings> {
    const response = await apiClient.get<UserSettings>('/api/v1/auth/settings')
    return response.data
  },

  /**
   * Update User Settings
   * PUT /api/v1/auth/settings
   */
  async updateUserSettings(params: UpdateUserSettingsParams): Promise<UserSettings> {
    const response = await apiClient.put<UserSettings>('/api/v1/auth/settings', params)
    return response.data
  },
}
